import React, {useState, useCallback} from 'react';
import {useMutation} from '@tanstack/react-query';
import {AdminService} from '@/services/adminService';
import Card from '@/components/common/Card';
import {Loader2} from 'lucide-react';

interface Props {
    className?: string;
}

interface PasswordResetState {
    passwordEmail: string;
    newPassword: string;
}

interface PasswordErrors {
    passwordLetters: boolean;
    passwordNumber: boolean;
    passwordLength: boolean;
}

interface Toast {
    error: boolean;
    message: string;
}

/**
 * PasswordReset component - Admin page for manual password resets
 * Refactored to follow docs/SUMMARY.md best practices
 */
export const PasswordReset: React.FC<Props> = () => {
    const [state, setState] = useState<PasswordResetState>({
        passwordEmail: '',
        newPassword: '',
    });

    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({
        passwordLetters: false,
        passwordNumber: false,
        passwordLength: false,
    });

    const [toast, setToast] = useState<Toast>({
        error: false,
        message: '',
    });

    // React Query mutation for password reset
    const resetPasswordMutation = useMutation({
        mutationFn: ({email, password}: {email: string; password: string}) =>
            AdminService.resetUserPassword(email, password),
        onSuccess: () => {
            setToast({
                error: false,
                message: 'Password reset successfully!',
            });
            setState({
                passwordEmail: '',
                newPassword: '',
            });
        },
        onError: (error) => {
            console.error('Password reset error:', error);
            setToast({
                error: true,
                message: 'Error resetting password. Please try again.',
            });
        },
    });

    const validatePassword = useCallback((password: string): PasswordErrors => {
        const letterReg = /[a-zA-Z]/;
        const numberReg = /[0-9]/;

        return {
            passwordLetters: !letterReg.test(password),
            passwordNumber: !numberReg.test(password),
            passwordLength: password.length < 8,
        };
    }, []);

    const handleInputChange = useCallback(
        (field: keyof PasswordResetState) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setState((prev) => ({
                ...prev,
                [field]: value,
            }));

            if (field === 'newPassword') {
                setPasswordErrors(validatePassword(value));
            }
        },
        [validatePassword]
    );

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setToast({error: false, message: ''});

            if (!state.passwordEmail.trim()) {
                setToast({
                    error: true,
                    message: 'Please enter an email address.',
                });
                return;
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(state.passwordEmail)) {
                setToast({
                    error: true,
                    message: 'Please enter a valid email address.',
                });
                return;
            }

            if (!state.newPassword.trim()) {
                setToast({
                    error: true,
                    message: 'Please enter a new password.',
                });
                return;
            }

            // Check password validation
            const errors = validatePassword(state.newPassword);
            if (errors.passwordLetters || errors.passwordNumber || errors.passwordLength) {
                setToast({
                    error: true,
                    message: 'Please fix password validation errors before submitting.',
                });
                return;
            }

            resetPasswordMutation.mutate({
                email: state.passwordEmail,
                password: state.newPassword,
            });
        },
        [state, validatePassword, resetPasswordMutation]
    );

    const hasPasswordErrors =
        passwordErrors.passwordLetters || passwordErrors.passwordNumber || passwordErrors.passwordLength;

    return (
        <div className="space-y-6">
            <Card>
                <Card.Header>
                    <Card.Title>Manual Password Reset</Card.Title>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="passwordEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                User Email Address
                            </label>
                            <input
                                id="passwordEmail"
                                type="email"
                                value={state.passwordEmail}
                                onChange={handleInputChange('passwordEmail')}
                                placeholder="Enter user email address"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                New Password
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                value={state.newPassword}
                                onChange={handleInputChange('newPassword')}
                                placeholder="Enter new password"
                                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-mld-primary focus:border-mld-primary ${
                                    hasPasswordErrors ? 'border-red-300' : 'border-gray-300'
                                }`}
                                required
                            />

                            {state.newPassword && hasPasswordErrors && (
                                <div className="mt-2 space-y-1">
                                    {passwordErrors.passwordLength && (
                                        <p className="text-sm" style={{color: '#F15E87'}}>
                                            • Password must be at least 8 characters long
                                        </p>
                                    )}
                                    {passwordErrors.passwordLetters && (
                                        <p className="text-sm" style={{color: '#F15E87'}}>
                                            • Password must contain at least one letter
                                        </p>
                                    )}
                                    {passwordErrors.passwordNumber && (
                                        <p className="text-sm" style={{color: '#F15E87'}}>
                                            • Password must contain at least one number
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                type="submit"
                                disabled={resetPasswordMutation.isPending || hasPasswordErrors}
                                className="flex items-center space-x-2 px-4 py-2 bg-mld-primary text-white rounded-md hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                {resetPasswordMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                                <span>{resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}</span>
                            </button>

                            {toast.message && (
                                <div
                                    className="px-3 py-2 rounded-md text-sm text-white"
                                    style={{backgroundColor: toast.error ? '#F15E87' : '#42D4B0'}}>
                                    {toast.message}
                                </div>
                            )}
                        </div>
                    </form>
                </Card.Content>
            </Card>

            <Card>
                <Card.Header>
                    <Card.Title>Password Requirements</Card.Title>
                </Card.Header>
                <Card.Content>
                    <div className="space-y-2 text-sm text-gray-600">
                        <p>Passwords must meet the following requirements:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>At least 8 characters long</li>
                            <li>Contains at least one letter (a-z, A-Z)</li>
                            <li>Contains at least one number (0-9)</li>
                        </ul>
                    </div>
                </Card.Content>
            </Card>
        </div>
    );
};

export default PasswordReset;
